/**
 * Preview-access grant persistence for storefront app-proxy traffic.
 *
 * Shopify strips Set-Cookie on app-proxy responses, so the server redirects
 * successful unlocks with `?fg_preview=<grant>`. We stash that in sessionStorage
 * and send it back on API calls via `X-FG-Preview-Grant`.
 */

const STORAGE_KEY = "fg.previewGrant";
const QUERY_PARAM = "fg_preview";

export function capturePreviewGrantFromUrl(): void {
  if (typeof window === "undefined") return;
  try {
    const url = new URL(window.location.href);
    const grant = url.searchParams.get(QUERY_PARAM);
    if (!grant?.trim()) return;
    sessionStorage.setItem(STORAGE_KEY, grant.trim());
    url.searchParams.delete(QUERY_PARAM);
    const cleaned = url.pathname + url.search + url.hash;
    window.history.replaceState(null, "", cleaned || url.pathname);
  } catch {
    /* ignore — private mode / blocked storage */
  }
}

export function readStoredPreviewGrant(): string | null {
  if (typeof window === "undefined") return null;
  try {
    const grant = sessionStorage.getItem(STORAGE_KEY);
    return grant?.trim() ? grant.trim() : null;
  } catch {
    return null;
  }
}

/**
 * Drop the stored grant. Called by the refresh-rehydration guard when we land on
 * the gate *with* a grant already in the URL (or an explicit `preview_error`) —
 * proof the stored grant is stale/rotated, so it must not drive another redirect
 * (otherwise the gate would loop forever).
 */
export function clearStoredPreviewGrant(): void {
  if (typeof window === "undefined") return;
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    /* ignore */
  }
}
